import { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useAuth } from '@/hooks/useAuth';
import { useWorkoutHistory } from '@/hooks/useWorkoutHistory';
import { WorkoutDayModal } from '@/components/WorkoutDayModal';
import { supabase } from '@/lib/supabase';
import { SessionSummary } from '@/lib/types';

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function toDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function HistoryScreen() {
  const { profile } = useAuth();
  const [studentId, setStudentId] = useState('');
  const today = new Date();
  const [displayYear, setDisplayYear] = useState(today.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<SessionSummary[]>([]);

  const { historyMap, loading } = useWorkoutHistory(studentId);

  useEffect(() => {
    if (!profile) return;
    supabase.from('students').select('id').eq('profile_id', profile.id).single()
      .then(({ data }) => { if (data) setStudentId(data.id); });
  }, [profile]);

  if (!studentId || loading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  const isCurrentMonth = displayYear === today.getFullYear() && displayMonth === today.getMonth();

  function prevMonth() {
    if (displayMonth === 0) { setDisplayMonth(11); setDisplayYear(y => y - 1); }
    else setDisplayMonth(m => m - 1);
  }

  function nextMonth() {
    if (isCurrentMonth) return;
    if (displayMonth === 11) { setDisplayMonth(0); setDisplayYear(y => y + 1); }
    else setDisplayMonth(m => m + 1);
  }

  const firstDay = new Date(displayYear, displayMonth, 1).getDay();
  const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthPrefix = `${displayYear}-${String(displayMonth + 1).padStart(2, '0')}`;
  const workoutsThisMonth = Object.keys(historyMap)
    .filter(k => k.startsWith(monthPrefix))
    .reduce((sum, k) => sum + historyMap[k].length, 0);

  const todayKey = toDateKey(today.getFullYear(), today.getMonth(), today.getDate());

  function handleDayPress(day: number) {
    const key = toDateKey(displayYear, displayMonth, day);
    const sessions = historyMap[key];
    if (!sessions?.length) return;
    setSelectedDate(key);
    setSelectedSessions(sessions);
  }

  return (
    <View style={styles.container}>
      <View style={styles.monthHeader}>
        <TouchableOpacity onPress={prevMonth} style={styles.navBtn}>
          <Text style={styles.navBtnText}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.monthTitle}>{MONTHS_PT[displayMonth]} {displayYear}</Text>
        <TouchableOpacity onPress={nextMonth} style={styles.navBtn} disabled={isCurrentMonth}>
          <Text style={[styles.navBtnText, isCurrentMonth && styles.navBtnDisabled]}>›</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.countBadge}>
        {workoutsThisMonth} {workoutsThisMonth === 1 ? 'treino' : 'treinos'} este mês
      </Text>

      <View style={styles.weekRow}>
        {WEEKDAYS.map((d, i) => <Text key={i} style={styles.weekDay}>{d}</Text>)}
      </View>

      <View style={styles.grid}>
        {cells.map((day, i) => {
          if (day === null) return <View key={i} style={styles.cell} />;
          const key = toDateKey(displayYear, displayMonth, day);
          const hasSessions = !!(historyMap[key]?.length);
          const isToday = key === todayKey;
          return (
            <TouchableOpacity
              key={i}
              style={styles.cell}
              onPress={() => handleDayPress(day)}
              activeOpacity={hasSessions ? 0.7 : 1}
            >
              <View style={[
                styles.dayCircle,
                hasSessions && styles.dayDone,
                isToday && !hasSessions && styles.dayToday,
              ]}>
                <Text style={[
                  styles.dayText,
                  hasSessions && styles.dayTextDone,
                  isToday && !hasSessions && styles.dayTextToday,
                ]}>{day}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {Object.keys(historyMap).length === 0 && (
        <Text style={styles.empty}>Nenhum treino concluído ainda.</Text>
      )}

      <WorkoutDayModal
        date={selectedDate}
        sessions={selectedSessions}
        visible={!!selectedDate}
        onClose={() => setSelectedDate(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5', padding: 16 },
  monthHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  monthTitle: { fontSize: 18, fontWeight: '700', color: '#1A1A1A' },
  navBtn: { padding: 8 },
  navBtnText: { fontSize: 24, color: '#C8102E', fontWeight: '700' },
  navBtnDisabled: { color: '#ccc' },
  countBadge: { fontSize: 12, color: '#C8102E', fontWeight: '600', marginBottom: 16, textAlign: 'center' },
  weekRow: { flexDirection: 'row', marginBottom: 4 },
  weekDay: { flex: 1, textAlign: 'center', fontSize: 12, fontWeight: '600', color: '#aaa' },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.285714%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center' },
  dayCircle: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center' },
  dayDone: { backgroundColor: '#C8102E' },
  dayToday: { borderWidth: 1.5, borderColor: '#C8102E' },
  dayText: { fontSize: 14, color: '#333' },
  dayTextDone: { color: '#fff', fontWeight: '700' },
  dayTextToday: { color: '#C8102E', fontWeight: '700' },
  empty: { textAlign: 'center', color: '#aaa', fontSize: 14, marginTop: 24 },
});
